import { supabase } from "@/integrations/supabase/client";
import { Store } from "@/models/storeModel";

/**
 * Fetches stores that the current authenticated user has access to
 */
export const fetchUserStores = async (): Promise<Store[]> => {
  try {
    // First get the current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      console.error("No authenticated user found");
      return [];
    }

    // Get user role first to determine access level
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("role")
      .eq("id", user.id)
      .single();

    if (userError) {
      console.error("Error fetching user role:", userError);
      return [];
    }

    // Super admins can see all stores
    if (userData.role === "super_admin") {
      const { data: allStores, error: storesError } = await supabase
        .from("stores")
        .select("*")
        .order("name");

      if (storesError) {
        console.error("Error fetching all stores:", storesError);
        return [];
      }

      return allStores.map((store) => ({
        ...store,
        id: store.id,
        name: store.name,
        address: store.address || "",
        phone: store.phone || "",
        email: store.email || "",
        is_active: store.is_active || true,
        created_at: store.created_at,
        updated_at: store.updated_at,
        business_hours: store.business_hours
          ? JSON.parse(store.business_hours as string)
          : undefined,
      }));
    }

    // Get stores where the user is listed as an employee (including ones they created)
    const { data: userStores, error: storesError } = await supabase
      .from("store_employees")
      .select(
        "store_id, stores:store_id(id, name, address, phone, email, is_active, created_at, updated_at)"
      )
      .eq("user_id", user.id);

    if (storesError) {
      console.error("Error fetching manager stores:", storesError);
      return [];
    }

    const stores = userStores
      .filter((item) => item.stores) // Filter out any null stores
      .map((item) => ({
        ...item.stores,
        id: item.stores.id,
        name: item.stores.name,
        address: item.stores.address || "",
        phone: item.stores.phone || "",
        email: item.stores.email || "",
        is_active: item.stores.is_active || true,
        created_at: item.stores.created_at,
        updated_at: item.stores.updated_at,
      }));

    return stores;
  } catch (error) {
    console.error("Error in fetchUserStores:", error);
    return [];
  }
};
